<script setup lang="ts">
import { computed } from 'vue'

/**
 * Loading placeholder.
 *
 * CLAUDE.md §11: "Loading = skeletons, never spinners-on-white." A skeleton
 * that matches the shape of the content it is standing in for keeps the layout
 * from jumping when data lands, which is the whole point — a spinner tells the
 * user to wait, a skeleton tells them what they are waiting for.
 *
 * The block is `aria-hidden`; announce the wait once on the container instead of
 * having every bar shout at a screen reader.
 */

const props = withDefaults(
  defineProps<{
    variant?: 'text' | 'block' | 'circle' | 'row'
    /** CSS width, e.g. `60%` or `8rem`. */
    width?: string
    /** CSS height. Defaults follow the variant. */
    height?: string
    /** Repeat count for `text` and `row`. */
    lines?: number
  }>(),
  { variant: 'text', width: '100%', lines: 1 },
)

const style = computed(() => ({
  width: props.width,
  ...(props.height === undefined ? {} : { height: props.height }),
}))
</script>

<template>
  <div v-if="lines > 1" class="e-skeleton-stack" aria-hidden="true">
    <span
      v-for="line in lines"
      :key="line"
      class="e-skeleton"
      :class="`e-skeleton--${variant}`"
      :style="{ ...style, width: line === lines ? '70%' : style.width }"
    />
  </div>
  <span
    v-else
    class="e-skeleton"
    :class="`e-skeleton--${variant}`"
    :style="style"
    aria-hidden="true"
  />
</template>

<style>
.e-skeleton {
  display: block;
  background-color: var(--erpx-color-surface-active);
  border-radius: var(--erpx-radius-sm);
  animation: e-skeleton-pulse 1.4s var(--erpx-easing-standard) infinite;
}

.e-skeleton--text {
  height: 0.75em;
  margin-block: 0.25em;
}

.e-skeleton--block {
  height: 6rem;
  border-radius: var(--erpx-radius);
}

.e-skeleton--circle {
  width: 2rem;
  height: 2rem;
  border-radius: var(--erpx-radius-full);
}

.e-skeleton--row {
  height: var(--erpx-row-h);
  border-radius: var(--erpx-radius-sm);
}

.e-skeleton-stack {
  display: flex;
  flex-direction: column;
  gap: var(--erpx-space-2);
  width: 100%;
}

@keyframes e-skeleton-pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.55; }
}

@media (prefers-reduced-motion: reduce) {
  .e-skeleton {
    animation: none;
  }
}
</style>
